import { EventSourcePolyfill } from 'event-source-polyfill';
import ItemClasificacion from './ItemClasificacion.js';
import { baseSse } from './sseBase.js';

// Stream SSE de la sincronización de descarga de datos (/eventos/events de core,
// evento "sync-progreso"). El backend (SyncServiceImpl) narra por qué paso va la
// sincronización y, en el modo streaming, envía cada colección (paginada las grandes)
// como eventos "datos" hasta un "fin" con los totales por colección.
// Solo se conecta mientras dura una sincronización y se cierra al terminar, para no
// dejar una conexión SSE extra abierta de forma permanente.
//
// Además de los eventos de negocio avisa de los cortes de la propia conexión (onConexion).
// El polyfill reconecta solo, pero lo que el servidor emitió mientras tanto se pierde, y
// la pantalla tiene que poder decirlo en vez de quedarse callada.

const subscribers = [];
const readyCallbacks = [];
const conexionCallbacks = [];
let eventSource = null;
let conectado = false;
let enCorte = false; // la conexión se ha caído y el polyfill está reintentando

function readToken() {
    try {
        const session = JSON.parse(localStorage.getItem('session') || '{}');
        return session.access_token || session.token || session.accessToken || '';
    } catch (e) {
        return '';
    }
}

function buildUrl() {
    return baseSse(ItemClasificacion.server_movilidad) + ItemClasificacion.prefix + 'eventos/events';
}

function notify(payload, meta) {
    subscribers.slice().forEach(function (cb) {
        try { cb(payload, meta); } catch (err) { console.error('SincronizacionEvents subscriber threw', err); }
    });
}

function notifyConexion(info) {
    conexionCallbacks.slice().forEach(function (cb) {
        try { cb(info); } catch (err) { console.error('SincronizacionEvents onConexion threw', err); }
    });
}

function onEvento(event) {
    let payload = null;
    try { payload = JSON.parse(event.data); } catch (err) { payload = { texto: event.data }; }
    // El tamaño en bruto es lo que permite medir a qué ritmo llegan los lotes.
    notify(payload, { bytes: (event.data || '').length, at: Date.now() });
}

function dispararReady() {
    conectado = true;
    const cbs = readyCallbacks.splice(0, readyCallbacks.length);
    cbs.forEach(function (cb) {
        try { cb(); } catch (err) { console.error('SincronizacionEvents onReady threw', err); }
    });
}

function ensureConnected() {
    if (eventSource) return;
    conectado = false;
    enCorte = false;
    const es = new EventSourcePolyfill(buildUrl(), {
        headers: { Authorization: 'Bearer ' + readToken() },
        heartbeatTimeout: 1800000
    });
    eventSource = es;
    // El backend (EventoSseService) envía "hello" al suscribirse: confirma que el emisor
    // del usuario ya está registrado en el servidor -> es seguro lanzar el disparador HTTP.
    es.addEventListener('hello', dispararReady);
    // Evento de negocio de la sincronización (buscando / datos / coleccion-fin / fin / error).
    es.addEventListener('sync-progreso', onEvento);
    // 'open' llega en la primera conexión y en cada reconexión tras un corte.
    es.addEventListener('open', function () {
        if (!enCorte) return;
        enCorte = false;
        notifyConexion({ estado: 'recuperada' });
    });
    es.addEventListener('error', function (err) {
        console.warn('[SincronizacionEvents] SSE conexión', err);
        // Tras close() el polyfill no vuelve a avisar, pero por si acaso: cerrado no es corte.
        if (eventSource !== es || es.readyState === es.CLOSED) return;
        // El polyfill dispara 'error' por cada reintento fallido; se avisa solo del primero.
        if (enCorte) return;
        enCorte = true;
        notifyConexion({ estado: 'perdida', error: err });
    });
}

export default {
    /**
     * Suscribe un callback que recibe cada payload (y un segundo argumento {bytes, at})
     * y abre la conexión (idempotente).
     */
    subscribe(callback) {
        if (typeof callback === 'function' && subscribers.indexOf(callback) < 0) {
            subscribers.push(callback);
        }
        ensureConnected();
    },
    unsubscribe(callback) {
        const i = subscribers.indexOf(callback);
        if (i >= 0) subscribers.splice(i, 1);
        // Sin suscriptores no hay razón para mantener la conexión SSE abierta (evita fugas
        // si el flujo de sincronización no llega a llamar a close()).
        if (!subscribers.length) this.close();
    },
    /**
     * Avisa de los cortes de la conexión SSE: { estado: 'perdida' } cuando se cae y el
     * polyfill empieza a reintentar, { estado: 'recuperada' } cuando vuelve a abrirse.
     */
    onConexion(callback) {
        if (typeof callback === 'function' && conexionCallbacks.indexOf(callback) < 0) {
            conexionCallbacks.push(callback);
        }
    },
    /** Si ahora mismo la conexión está caída y reintentando. */
    enCorte() {
        return enCorte;
    },
    /**
     * Ejecuta el callback cuando la conexión SSE está establecida (evento "hello").
     * Si ya está conectada, lo ejecuta de inmediato.
     */
    onReady(callback) {
        if (typeof callback !== 'function') return;
        if (conectado) { try { callback(); } catch (e) { console.error(e); } return; }
        readyCallbacks.push(callback);
        ensureConnected();
    },
    /** Cierra el stream (se llama al terminar la sincronización para no dejar la conexión abierta). */
    close() {
        subscribers.length = 0;
        readyCallbacks.length = 0;
        conexionCallbacks.length = 0;
        conectado = false;
        enCorte = false;
        if (eventSource) {
            try { eventSource.close(); } catch (e) {}
            eventSource = null;
        }
    }
};
